/**
 * Mega Menu Renderer
 * @description 행 기반 레이아웃의 대형 드롭다운 메가 메뉴
 */

import { $, $$ } from '../../../../core';
import type { Category, SideMenuElements } from '../types';
import type { SideMenuRenderer } from './types';

const COLUMNS_PER_ROW = 6;

/**
 * 2뎁스 셀 HTML 생성 (2뎁스 타이틀 + 3뎁스 링크들)
 */
const renderSectionCell = (section: Category | undefined): string => {
  if (!section) {
    return '<div class="nde-c-side-menu__mega-menu-cell"></div>';
  }

  const hasChildren = section.children && section.children.length > 0;

  const childrenHtml = hasChildren
    ? `<ul class="nde-c-side-menu__mega-menu-list">
        ${(section.children ?? [])
          .map(
            (child) => `
          <li>
            <a href="${child.href}" class="nde-c-side-menu__mega-menu-link">${child.label}</a>
          </li>
        `
          )
          .join('')}
      </ul>`
    : '';

  return `
    <div class="nde-c-side-menu__mega-menu-cell">
      <a href="${section.href}" class="nde-c-side-menu__mega-menu-sub-title">${section.label}</a>
      ${childrenHtml}
    </div>
  `;
};

/**
 * 1뎁스 타이틀 셀 HTML 생성
 */
const renderTitleCell = (category: Category): string => {
  return `
    <div class="nde-c-side-menu__mega-menu-cell">
      <a href="${category.href}" class="nde-c-side-menu__mega-menu-title">${category.label}</a>
    </div>
  `;
};

/**
 * 메가메뉴 그리드 HTML 생성 (행 기반)
 */
const renderMegaMenuGrid = (columns: Category[]): string => {
  // 6개씩 나눠서 처리
  const rowGroups: Category[][] = [];
  for (let i = 0; i < columns.length; i += COLUMNS_PER_ROW) {
    rowGroups.push(columns.slice(i, i + COLUMNS_PER_ROW));
  }

  return rowGroups
    .map((group) => {
      // 이 그룹에서 가장 많은 2뎁스 섹션 개수
      const maxSectionCount = Math.max(...group.map((col) => col.children?.length ?? 0));

      // 행1: 1뎁스 타이틀들
      const titleRow = `
      <div class="nde-c-side-menu__mega-menu-row nde-c-side-menu__mega-menu-row--title">
        ${group.map((col) => renderTitleCell(col)).join('')}
      </div>
    `;

      // 행2~N: 2뎁스 섹션들
      const sectionRows = Array.from({ length: maxSectionCount })
        .map((_, sectionIndex) => {
          const cells = group.map((col) => {
            const section = col.children?.[sectionIndex];
            return renderSectionCell(section);
          });

          return `
          <div class="nde-c-side-menu__mega-menu-row">
            ${cells.join('')}
          </div>
        `;
        })
        .join('');

      return titleRow + sectionRows;
    })
    .join('');
};

/**
 * 카테고리 렌더링 (메가메뉴 형태)
 */
const render = (list: HTMLElement, categories: Category[]): void => {
  list.innerHTML = categories
    .map((category) => {
      const hasChildren = category.children && category.children.length > 0;
      const activeClass = category.active ? ' nde-c-side-menu__link--active' : '';
      const targetAttr = category.isOpenNewTab ? ' target="_blank" rel="noopener noreferrer"' : '';

      let megaMenuHtml = '';
      if (hasChildren) {
        megaMenuHtml = `
          <div class="nde-c-side-menu__mega-menu">
            <div class="nde-c-side-menu__mega-menu-grid">
              ${renderMegaMenuGrid(category.children ?? [])}
            </div>
          </div>
        `;
      }

      return `
        <li class="nde-c-side-menu__item">
          <a href="${category.href}"${targetAttr} class="nde-c-side-menu__link${activeClass}">
            ${category.label}
          </a>
          ${megaMenuHtml}
        </li>
      `;
    })
    .join('');
};

/**
 * 메가메뉴 인터랙션 설정
 */
const setupInteractions = ({ elements }: { elements: SideMenuElements; categories: Category[] }): void => {
  const { root, list } = elements;
  const items = $$<HTMLElement>('.nde-c-side-menu__item', list);
  let currentOpenMegaMenu: HTMLElement | null = null;
  let megaMenuTimeout: number | null = null;

  const closeMegaMenu = (): void => {
    if (currentOpenMegaMenu) {
      currentOpenMegaMenu.parentElement?.classList.remove('nde-c-side-menu__item--open');

      currentOpenMegaMenu.style.position = '';
      currentOpenMegaMenu.style.zIndex = '';
      currentOpenMegaMenu.style.top = '';
      currentOpenMegaMenu.style.left = '';
      currentOpenMegaMenu.style.right = '';
      currentOpenMegaMenu.style.width = '';
      currentOpenMegaMenu.style.display = '';

      currentOpenMegaMenu = null;
    }
  };

  for (const item of items) {
    const megaMenu = $<HTMLElement>('.nde-c-side-menu__mega-menu', item);
    if (!megaMenu) {
      continue;
    }

    item.addEventListener('mouseenter', () => {
      if (megaMenuTimeout) {
        window.clearTimeout(megaMenuTimeout);
        megaMenuTimeout = null;
      }

      if (currentOpenMegaMenu && currentOpenMegaMenu !== megaMenu) {
        closeMegaMenu();
      }

      const rootRect = root.getBoundingClientRect();
      const MEGA_MENU_GAP = 19;

      megaMenu.style.position = 'fixed';
      megaMenu.style.zIndex = '1100';
      megaMenu.style.top = `${rootRect.bottom + MEGA_MENU_GAP}px`;
      megaMenu.style.left = '0';
      megaMenu.style.right = '0';
      megaMenu.style.width = 'auto';
      megaMenu.style.display = 'block';

      item.classList.add('nde-c-side-menu__item--open');
      currentOpenMegaMenu = megaMenu;
    });

    item.addEventListener('mouseleave', (e) => {
      const relatedTarget = e.relatedTarget as Node | null;
      if (megaMenu.contains(relatedTarget)) {
        return;
      }

      megaMenuTimeout = window.setTimeout(() => {
        closeMegaMenu();
      }, 300);
    });

    megaMenu.addEventListener('mouseenter', () => {
      if (megaMenuTimeout) {
        window.clearTimeout(megaMenuTimeout);
        megaMenuTimeout = null;
      }
    });

    megaMenu.addEventListener('mouseleave', () => {
      megaMenuTimeout = window.setTimeout(() => {
        closeMegaMenu();
      }, 300);
    });
  }

  window.addEventListener(
    'scroll',
    () => {
      if (currentOpenMegaMenu) {
        closeMegaMenu();
      }
    },
    { passive: true }
  );

  document.addEventListener('click', (e) => {
    if (!root.contains(e.target as Node) && currentOpenMegaMenu) {
      closeMegaMenu();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && currentOpenMegaMenu) {
      closeMegaMenu();
    }
  });
};

export const megaMenuRenderer: SideMenuRenderer = {
  render,
  setupInteractions,
};
